import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { createSearchByObject, hasRole, padZeroLeft } from '@synergy/utils';
import urlJoin from 'url-join';

import { AppState } from '../../../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list';
import { ContactsAndOrganizationsApi } from '../../../../api/contacts-and-organizations-api';
import { ParentSupplier } from '../models/parent-supplier';
import { PoolAutocompleteListAction } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list-action';
import { PoolAutocompleteListFooter } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../../../../../common/components/abstract/form-autocomplete-list';
import { ROUTES } from '../../../../../sidebar-menu/navigation-drawer/routes';
import { User } from '../../../../../../common/models/user';

export interface PartialParentSupplier extends BaseAutocompleteSuggestionType {
    name: string;
}

interface ParentSupplierAutocompleteListProps {
    supplierId?: number;
    user: User;
}

class ParentSupplierAutocompleteListBase extends PoolFormAutocompleteList<
    ParentSupplier,
    PartialParentSupplier,
    ParentSupplierAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new ContactsAndOrganizationsApi(API_BASE_URL);

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.autocomplete.footer.suppliers"
            url={urlJoin(BASE_URL, ROUTES.contactsAndOrganizations.suppliers.new)}
        />
    );

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public componentDidUpdate(prevProps: ParentSupplierAutocompleteListProps) {
        if (this.props.supplierId !== prevProps.supplierId) {
            this.setState({ items: [] });
        }
    }

    public getSuggestionValue = (suggestion: PartialParentSupplier) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        if (!this.props.supplierId) {
            this.setState({ items: [] });
            return;
        }
        const response = await this.api.Supplier.SupplierDetail.Overview.getParentSuppliers(
            this.props.supplierId,
            createSearchByObject({ search })
        );

        const items = response.success === true ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: ParentSupplier[]): PartialParentSupplier[] =>
        items.map(parentSupplier => {
            // Using double slash + hashtag, because urljoin removes leading slash from /#
            const editLink = urlJoin(
                BASE_URL,
                ROUTES.contactsAndOrganizations.suppliers.detail.replace(':id', parentSupplier.id.toString())
            );
            return {
                id: padZeroLeft(parentSupplier.id, 5),
                name: parentSupplier.name,
                city: parentSupplier.mainAddress.city,
                actions: <PoolAutocompleteListAction url={editLink} allowedRoles={this.actionRoles} />,
            };
        });

    public getDisplayValue(parentSupplier: ParentSupplier) {
        return parentSupplier.name;
    }
}

const mapStateToProps = (state: AppState) => ({
    inProgress: state.suppliers.requesting,
    user: state.user,
});

export const ParentSupplierAutocompleteList = connect(mapStateToProps, {})(ParentSupplierAutocompleteListBase);
